import { useRef } from 'react'
import { useVisemeEnvelope } from '../useVisemeEnvelope'
import { useVizCanvas } from '../useVizCanvas'
import { withAlpha } from '../vizColor'

const SAMPLES = 180
const NOISE = 0.06

// Oscilloscope: a scrolling, mirrored, glow-filled waveform. The polished
// descendant of the original SpeechScope — same control-panel reading, more
// bloom and a soft fill under the trace.
export function ScopeViz({ character, color, anyVoice }) {
  const { read, activeRef, reduceMotion } = useVisemeEnvelope(character, { anyVoice })
  const bufRef = useRef(new Float32Array(SAMPLES))

  const draw = (ctx, { w, h, amp, active, t }) => {
    const buf = bufRef.current
    const mid = h / 2
    const jitter = active && amp > 0.04 ? Math.sin(t * 0.55) * NOISE * amp : 0
    buf.copyWithin(0, 1)
    buf[SAMPLES - 1] = Math.max(0, Math.min(1, amp + jitter))

    // Center baseline.
    ctx.strokeStyle = 'rgba(255,255,255,0.08)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, mid)
    ctx.lineTo(w, mid)
    ctx.stroke()

    const ampPx = mid * 0.9
    const xOf = (i) => (i / (SAMPLES - 1)) * w

    // Soft fill between the mirrored halves.
    ctx.beginPath()
    for (let i = 0; i < SAMPLES; i++) ctx.lineTo(xOf(i), mid - buf[i] * ampPx)
    for (let i = SAMPLES - 1; i >= 0; i--) ctx.lineTo(xOf(i), mid + buf[i] * ampPx)
    ctx.closePath()
    ctx.fillStyle = withAlpha(color, 0.16)
    ctx.fill()

    // Glowing trace, both halves.
    ctx.strokeStyle = color
    ctx.lineWidth = 2
    ctx.lineJoin = 'round'
    ctx.shadowColor = color
    ctx.shadowBlur = 8
    for (const sign of [-1, 1]) {
      ctx.beginPath()
      for (let i = 0; i < SAMPLES; i++) {
        const x = xOf(i)
        const y = mid + sign * buf[i] * ampPx
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      }
      ctx.stroke()
    }
    ctx.shadowBlur = 0

    // Reading head.
    const headY = buf[SAMPLES - 1] * ampPx
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.arc(w - 1, mid - headY, 1.8, 0, Math.PI * 2)
    ctx.arc(w - 1, mid + headY, 1.8, 0, Math.PI * 2)
    ctx.fill()
  }

  const ref = useVizCanvas({ read, activeRef, reduceMotion, draw })
  return <canvas ref={ref} style={{ display: 'block', width: '100%', height: '100%' }} />
}
